/**
 * WCC360 v2 — band copy.
 *
 * ELEVEN screens are restemmed for the 15–18 band. Every slot, every
 * scoring rule and every option weight stays IDENTICAL — only the wording
 * changes. That is the whole point: a 16-year-old and a 25-year-old are
 * being measured on the same scales, described in the language of the life
 * they're actually living.
 *
 * If a change here would alter what a screen writes to a slot, it doesn't
 * belong here — it belongs in the item set.
 *
 * Voice: second person, present tense. Becoming, not are. Still deciding,
 * not unsure. No countdowns, no scarcity, nothing implying a closed door.
 *
 * ── CANON-PENDING · UNRATIFIED AMENDMENT TO X-STUDY-002 ──────────────────
 * `p1_reality_check` carries a FIFTH option — "I'd do both, study and earn"
 * — mapped to part-time study capacity. Approved by Yv, NOT yet ratified in
 * the Item Bank. Recorded here, at the option site in
 * `screens/Part1Screens.tsx`, and in both reality maps in `types.ts`. Same
 * convention as the domain-gate change in the recommendation engine.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * JARGON RULE: any word a 12-year-old wouldn't know is either replaced or
 * glossed inline — grade 6 for 12–14, grade 8 for 15–18. Never assumed.
 */
import { useAssessmentV2 } from "./store";
import type { Band } from "./band";

export interface ScreenCopy {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  /** ScenarioChoice second-person setup line. */
  situation?: string;
  /** Slider ends. */
  leftLabel?: string;
  rightLabel?: string;
  /** Option / card / offer id → replacement label. */
  options?: Record<string, string>;
  /** Option ids to remove entirely for this band. */
  hideOptions?: string[];
  /** FrequencyBlock row id → replacement question. */
  rows?: Record<string, string>;
  /**
   * Prompt id → a short, neutral concrete example so an abstract item can
   * be pictured. Never aspirational, never off-putting, never a nudge
   * towards yes or no.
   */
  examples?: Record<string, string>;

}


/**
 * The eleven restems. Screen ids are the store's, the comment gives the
 * canonical item code.
 */
const COPY_15_18: Record<string, ScreenCopy> = {
  // X-STUDY-001 — same four ranks, same MIN rule. "Before you start earning"
  // is the frame that makes sense when you haven't started.
  p1_study_appetite: {
    title: "How long are you willing to study before you start earning?",
    subtitle: "Every one of these leads somewhere real.",
    options: {
      none: "Start working as soon as I can",
      short_6mo: "1–2 years (diploma or certificate)",
      part_time_1_2y: "3–5 years (a degree)",
      full_degree: "5+ years (professional degree or research)",
    },
  },

  // X-STUDY-002 — the corroborator. Still MIN(stated, revealed). At this age
  // the pressure isn't money or time, it's watching your friends move first.
  p1_reality_check: {
    title: "Your closest friends all start earning right after school. The path you want takes five more years. Honestly, what happens?",
    subtitle: "",
    options: {
      would_sign_up: "I'd take the five years",
      want_numbers: "I'd want to know if it pays off first",
      // CANON-PENDING (unratified amendment to X-STUDY-002) — the fifth
      // option, mapped to part-time study capacity. See types.ts.
      study_and_work: "I'd study and work at the same time",
      cant_time: "I'd find a shorter route in",
      cant_money: "I'd start earning with them",
    },
    hideOptions: ["cant_neither"],
  },



  // W-SORT-001 — the six workdays stay. Aspiration is fine at this age;
  // it just needs framing so they don't read it as "your life now".
  p2_workday_sort: {
    eyebrow: "Imagine you're already working",
    title: "Drag these workdays into your order — the one you'd most want at the top.",
    subtitle: "None of this is happening yet. Rank them as the you that's coming.",
  },

  // W-OFFER-001 — relative money only. A teenager has no anchor for an LPA
  // figure, so "much more" and "less" are the honest units.
  p2_two_offers: {
    eyebrow: "Same start date",
    title: "Two jobs. Same start date. Which do you take?",
    subtitle: "Not which is more sensible. Which one, if you had to choose today.",
    options: {
      income: "Pays much more, work you'd find ordinary",
      loveLowPay: "Pays less, work you'd genuinely love",
      // "CV" is the jargon Yv caught. Glossed, not assumed.
      prestige: "A place everyone recognises when you say where you work",
      stability: "Guaranteed to still be there in 10 years",
      impact: "Work where you see the people you helped",
      autonomy: "You decide how and when you work",
    },

  },

  // W-TRADE-002 — the contract pair means nothing before your first job.
  // Same stability slot, same 100/0 split, framed as longevity.
  p2_trade_offs: {
    eyebrow: "Three more",
    title: "Same job, same pay — which version do you take?",
    options: {
      two_yr: "A job that pays 30% more but might not last",
      permanent: "A job you know will still exist in ten years",
    },
  },

  // W-REALITY-001 — the corroboration engine. Same seven rows, same slots,
  // same values, all school-framed.
  p2_last_month: {
    eyebrow: "What actually happened",
    title: "Thinking about the last month at school — not how you'd like things to be.",
    subtitle: "What you actually did beats what you're comfortable with in principle.",
    rows: {
      r_screen: "Hours a day you were already on a screen",
      r_stress: "Days school stress followed you home",
      r_speak: "Times you spoke in front of your class or year group",
      r_travel: "Nights away from home — trips, camps, family",
      r_sit: "Share of the day you were sitting in class",
      r_switch: "Times a day you got interrupted while studying",
      r_evening: "Evenings and weekends you had to work on schoolwork",
    },
  },

  // W-SPEAK-001
  p2_presenting: {
    eyebrow: "Standing up to talk",
    title: "You have to present to your whole year group next week. Your first reaction is…",
    subtitle: "How you'd actually feel — not how you think you should feel.",
    leftLabel: "I'd rather talk to one person",
    rightLabel: "Honestly, fine by me",
  },

  // W-HARDDAY-001 — same four options, same values. Work stress becomes
  // the thing that actually loads a teenager: carrying someone else's day.
  p2_hard_day: {
    eyebrow: "How you recover",
    title: "A friend's been going through something hard and you spent the day with them. That evening you…",
    situation: "It's stayed with you. Nobody's asking anything more of you tonight. What do you actually do?",
    options: {
      people: "Find my people — talk it out with someone I trust",
      move: "Get moving — walk, sport, ride it off",
      quiet: "Straight to my room. Silence, and I need it.",
      outwork: "Put it into something else and out-work it",
    },
  },

  // W-CLINICAL-001 — kept as-is. This gates a third of healthcare and
  // 16-year-olds pick medicine constantly. Matter-of-fact, never framed
  // around fear.
  p2_collapse: {},

  // W-SCHED-001
  p2_working_pattern: {
    title: "How would you want your working week to run?",
    subtitle: "Not what school is like. What you'd choose, once it's up to you.",
  },

  // W-BATTERY-001
  p2_people_battery: {
    eyebrow: "Your energy, honestly",
    title: "After a full day of constant people — class, corridors, group work — are you charged up or drained?",
  },

  // W-GRIND-001
  p2_grind_test: {
    eyebrow: "The unglamorous part",
    title: "Every job has parts nobody posts about. Which of these could you genuinely live with?",
    subtitle: "Pick as many as are true. Picking none is a real answer.",
  },

  // W-BODY-001 — same six prompts, same six slots, same yes/no values.
  // Grade-8 wording (no "desk-bound", no "bodily fluids") plus neutral
  // concrete examples on the three items a teenager can't picture.
  // b2, b4 and b6 get no example — see the note in Part2Screens.tsx.
  p2_quick_ones: {
    eyebrow: "Quick ones",
    title: "Yes or no. Go with your first reaction.",
    options: {
      b1: "I'd pick work that keeps me outdoors or moving over sitting at a desk",
      b2: "I work best when I can move around instead of sitting all day",
      b3: "I'm fine spending most of the working day on a screen",
      b4: "I'd be happy in work that means travelling a lot",
      b5: "I'm comfortable working close up with people's bodies",
      b6: "I stay calm around blood, or someone in a lot of pain",
    },
    examples: {
      b1: "like a gardener or a site supervisor",
      b3: "like an accountant or a graphic designer",
      b5: "like a dentist or a physiotherapist",
    },
  },
};


/**
 * 12–14 "Explorers" — all 22 Part 2 screens restemmed.
 *
 * Same screens, same option ids, same weights, same slots. New words.
 *
 * Gates from the Assessment Design Manual v2.0, all mandatory and all
 * checked by hand on every stem below:
 *   · Flesch-Kincaid grade ≤ 6.0
 *   · stem ≤ 18 words
 *   · one idea per stem — no "and"-joined propositions
 *   · no escape-hatch options ("not sure", "it depends")
 *   · no workplace vocabulary — no pay, promotion, manager, client,
 *     work deadline, or the word "career"
 *
 * Where the adult framing has no honest childhood analogue, the situation
 * moves into a twelve-year-old's life and the MEASUREMENT stays put. A
 * restem that changes what a screen measures is a bug, not a rewrite.
 */
const COPY_12_14: Record<string, ScreenCopy> = {
  // W-SORT-001 — same six cards, same slot map, same rank weights. Framed
  // as grown-up days, because a child ranking "my workday" is nonsense.
  p2_workday_sort: {
    eyebrow: "Grown-up you",
    title: "Put these days in order. Your favourite goes on top.",
    subtitle: "None of this has happened yet. Choose as the grown-up you.",
    options: {
      specialists: "With a small team who are really good at one thing",
      collab: "In a busy room full of people working together",
      solo: "On my own, deep in one thing",
      public: "Out with people who need help",
      mobile: "Moving between different places all day",
      maker: "In a quiet workshop or studio, making things",
    },
  },

  // W-OFFER-001 — the tournament and its tags are untouched. Two jobs
  // becomes two clubs: the same trade-offs, in the only place a child has
  // ever actually met them.
  p2_two_offers: {
    eyebrow: "Same day, same time",
    title: "Two clubs. You can only join one.",
    subtitle: "Not the sensible one. The one you would really pick.",
    options: {
      income: "A club that gives out prizes and rewards",
      loveLowPay: "A club with no prizes, but you love it",
      autonomy: "A club where you decide what to do",
      prestige: "A club everyone at school has heard of",
      stability: "A club you know will still be there next year",
      impact: "A club where you can see who you helped",
    },
  },

  // W-TRADE-002 — same three pairs, same 100/0 splits.
  p2_trade_offs: {
    eyebrow: "Three more",
    title: "Same project. Which way would you want it?",
    options: {
      no_check: "Nobody checks how you do it",
      daily: "A teacher checks your work every day",
      two_yr: "A club that is more fun but might stop soon",
      permanent: "A club that is less fun but always there",
      method: "You get clear steps to follow",
      your_way: "You work out your own way to do it",
    },
  },

  // W-BODY-001 — six swipes, same six slots. b6 keeps the sensory
  // tolerance measure but drops the clinical imagery: for a child, "someone
  // is hurt or upset" is the same tolerance they have actually met.
  p2_quick_ones: {
    eyebrow: "Six quick ones",
    title: "Yes or no. Go with your first feeling.",
    options: {
      b1: "I would rather be outside than at a desk",
      b2: "I think better when I can move around",
      b3: "I am happy spending most of the day on a screen",
      b4: "I would love to travel to new places often",
      b5: "I am comfortable being close to people who need help",
      b6: "I stay calm when someone is hurt or upset",
    },
    // Neutral, ordinary jobs a twelve-year-old has met. b2, b4 and b6 get
    // no example — no neutral one exists (see Part2Screens.tsx).
    examples: {
      b1: "like a gardener or a sports coach",
      b3: "like someone who designs things on a computer",
      b5: "like a dentist or a nurse",
    },
  },


  // W-REALITY-001 — the corroboration engine. Same seven rows, same slots,
  // same values. School and home, because that is their whole month.
  p2_last_month: {
    eyebrow: "What really happened",
    title: "Think about the last month at school and at home.",
    subtitle: "What you did counts for more than what you like.",
    rows: {
      r_screen: "Hours a day you were on a screen",
      r_stress: "Days school worries followed you home",
      r_speak: "Times you spoke in front of your class",
      r_travel: "Nights you slept away from home",
      r_sit: "How much of the day you sat still",
      r_switch: "Times a day something stopped you working",
      r_evening: "Evenings or weekends you had homework",
    },
  },

  // W-GRIND-001 — the boring part of something they chose to do anyway.
  p2_grind_test: {
    eyebrow: "The boring bit",
    title: "Even things you love have boring bits. Which could you put up with?",
    subtitle: "You do not have to enjoy it. You just have to cope with it. Picking none is fine.",
    options: {
      admin: "Tidying up and writing things down",
      physical: "Doing the same thing over and over",
      difficult: "People being rude or saying no to you",
      solo_slow: "Working alone for ages before anything happens",
      deadlines: "Finishing something fast when time runs out",
      on_feet: "Being on your feet all day",
    },
  },

  // W-CLINICAL-001 — same two slots, same conservative MIN, same four
  // option values. Blood and bodily fluids are out; the tolerance being
  // measured is calm around another person's distress, and a playground
  // fall is a situation every twelve-year-old has already seen.
  p2_collapse: {
    eyebrow: "One quick scene",
    title: "A friend falls hard in the playground and hurts their leg.",
    situation: "They are crying and cannot get up. A teacher is coming. What do you do?",
    options: {
      straight_in: "I go straight in and help them",
      feel_but_help: "It upsets me, but I still help",
      call_and_talk: "I get a grown-up and stay talking to them",
      struggle: "I find it too much. Someone else helps.",
    },
  },

  // W-BADDAY-001 — highest-evidence archetype signal. Same five cards.
  p2_bad_day: {
    eyebrow: "Nobody is watching",
    title: "On a bad day, what would you still enjoy doing?",
    subtitle: "Pick the best one first, then the next best.",
    options: {
      help: "Helping or fixing something for someone",
      puzzle: "Working out a really hard puzzle",
      make: "Making something beautiful or new",
      win: "Winning, or finishing what you started",
      build: "Building something that lasts",
    },
  },

  // W-PROBLEM-001 — sets the impact domain. Same five domains.
  p2_whose_problem: {
    eyebrow: "The people you would show up for",
    title: "Whose problem would you most want to fix?",
    subtitle: "Your first pick counts the most.",
    options: {
      care: "People who are ill or need looking after",
      grow: "People starting something new of their own",
      planet: "The planet, animals and the future",
      learn: "People who want to learn something new",
      systems: "Places and groups that need to run better",
    },
  },

  // W-ROOM-001 — nominal tag only, never scored.
  p2_room: {
    title: "Which team would you most want to join?",
    subtitle: "This only changes how we explain things later.",
    options: {
      calm: "Calm and kind, people come first",
      fast: "Fast and competitive, everyone wants to win",
      clear: "Clear and organised, everyone knows the plan",
      warm: "Friendly and relaxed, everyone joins in",
    },
  },

  // W-FOCUS-001
  p2_focus_slider: {
    eyebrow: "How your brain likes to work",
    title: "One thing at a time, or lots of things at once?",
    leftLabel: "One thing at a time",
    rightLabel: "Lots on the go",
  },

  // W-BATTERY-001
  p2_people_battery: {
    eyebrow: "Your energy, honestly",
    title: "After a whole day with people, are you buzzing or tired?",
    leftLabel: "Really tired",
    rightLabel: "Buzzing",
  },

  // W-HARDDAY-001 — same four options, same three slots, same values.
  p2_hard_day: {
    eyebrow: "How you get back to normal",
    title: "A friend had a hard day and you stayed with them.",
    situation: "It stays in your head. Nobody needs anything else tonight. What do you do?",
    options: {
      people: "Talk it out with someone I trust",
      move: "Go outside and move around",
      quiet: "Go to my room. I need the quiet.",
      outwork: "Put it into something else and keep busy",
    },
  },

  // W-SCHED-001 — same four options, same schedule values.
  p2_working_pattern: {
    title: "How would you like your week to go?",
    subtitle: "Not how school is. How you would choose it.",
    options: {
      regular: "Same times every day, evenings are mine",
      mostly: "Mostly the same, a few late nights",
      bursts: "Quiet weeks, then really busy ones",
      onDemand: "Whenever it is needed, any time",
    },
  },

  // W-SPEAK-001
  p2_presenting: {
    eyebrow: "Standing up to talk",
    title: "You have to talk in front of your class next week.",
    subtitle: "How you would really feel, not how you should feel.",
    leftLabel: "I would rather talk to one person",
    rightLabel: "Honestly, that is fine",
  },

  // W-TENYEAR-001 — same six cards, same archetype weights.
  p2_ten_years: {
    eyebrow: "Grown-up you",
    title: "When you are grown up, what would make you proud?",
    subtitle: "Pick the best one first, then the next best.",
    options: {
      built: "Built something people can see and use",
      cracked: "Solved a problem nobody else could",
      moved: "Made something that made people feel something",
      lifted: "Known for helping the people around me",
      led: "The one who decides what happens next",
      changed: "Changed something in the world that was unfair",
    },
  },

  // W-WRONG-001 — same emotional and grunt values. A late-night deck
  // becomes the group project, which is the same test of who carries it.
  p2_scenario_deadline: {
    eyebrow: "The night before",
    title: "Your group project is due tomorrow. You find a big mistake.",
    situation: "Everyone else has gone home. It can be fixed, but it takes hours.",
    options: {
      stay: "I stay and fix it tonight",
      plan: "I plan it with them and start early tomorrow",
      flag: "I tell them about it, but I let them fix it",
      leave: "They made the mistake, so they should fix it",
    },
  },

  // W-MONTH-001
  p2_scenario_month_off: {
    eyebrow: "A whole month",
    title: "A whole month off school. Nobody expects anything.",
    situation: "What really fills it? First thought.",
    options: {
      build: "Build or fix something I keep putting off",
      learn: "Learn everything about something I love",
      make: "Make something — writing, film, music, art",
      travel: "Go to new places and meet new people",
      volunteer: "Help out somewhere that matters to me",
    },
  },

  // W-ROCKET-001
  p2_scenario_failure: {
    eyebrow: "Six months later",
    title: "Something you led went wrong in front of everyone.",
    situation: "Six months later. Where are you, honestly?",
    options: {
      next: "Running the next try, and doing it better",
      under: "Learning from someone I look up to",
      stable: "Doing something safe while I think",
      different: "Trying something completely different",
    },
  },

  // W-STRUGGLE-001
  p2_scenario_teammate: {
    eyebrow: "Everyone can see it",
    title: "Someone in your group is quietly falling behind.",
    situation: "Nobody says it out loud. What do you do first?",
    options: {
      aside: "Ask them quietly what is going on",
      cover: "Quietly do a bit extra until they catch up",
      loop: "Tell the teacher, because they can help more than I can.",
      focus: "Get on with my own work",
    },
  },

  // W-BLANK-001
  p2_scenario_open_week: {
    eyebrow: "A blank week",
    title: "A whole week free. Nothing you have to do.",
    situation: "What really fills it? First thought, not the sensible one.",
    options: {
      make: "Making something new",
      crack: "Working out a problem I keep thinking about",
      meet: "Meeting people who do what I want to do",
      order: "Getting everything organised — plans and lists",
    },
  },

  // W-CREATE-001
  p2_creative_room: {
    eyebrow: "How much room",
    title: "Clear steps, or a blank page?",
    subtitle: "Where do you do your best work?",
    leftLabel: "Give me clear steps",
    rightLabel: "Give me a blank page",
  },

  // W-CONFIRM-001 — review. Same bullets, gentler frame.
  p2_here_is_what: {
    title: "Here is what we heard.",
    subtitle: "Change anything that does not sound like you.",
  },
};

const EMPTY: ScreenCopy = {};

export function copyFor(band: Band, screenId: string): ScreenCopy {
  if (band === "15to18") return COPY_15_18[screenId] ?? EMPTY;
  if (band === "12to14") return COPY_12_14[screenId] ?? EMPTY;
  return EMPTY;
}

/**
 * Screen-level copy resolver. Every Part 2 screen calls this and falls back
 * to its own 18+ wording, so a screen with no override is unchanged.
 */
export function useScreenCopy(screenId: string) {
  const { band } = useAssessmentV2();
  const c = copyFor(band, screenId);
  return {
    band,
    /** Field override, or the 18+ default. */
    t<K extends keyof ScreenCopy>(key: K, fallback: string): string {
      const v = c[key];
      return typeof v === "string" ? v : fallback;
    },
    /** Option label override by option id. */
    opt(id: string, fallback: string): string {
      return c.options?.[id] ?? fallback;
    },
    /** FrequencyBlock row question override by row id. */
    row(id: string, fallback: string): string {
      return c.rows?.[id] ?? fallback;
    },
    /** Neutral concrete example for a prompt id, if this band has one. */
    ex(id: string, fallback?: string): string | undefined {
      return c.examples?.[id] ?? fallback;
    },

    /**
     * Relabel and filter a ChoiceScreen option list. Values are never
     * touched — the same option value scores identically in both bands.
     */
    choices<V extends string>(
      list: readonly { value: V; label: string; hint?: string }[],
    ): { value: V; label: string; hint?: string }[] {
      return list
        .filter((o) => !c.hideOptions?.includes(o.value))
        .map((o) => ({ ...o, label: c.options?.[o.value] ?? o.label }));
    },
  };

}

export type ScreenCopyApi = ReturnType<typeof useScreenCopy>;

/** Relabel a list of `{ id, label }` records in place-safe fashion. */
export function relabel<T extends { id: string; label?: string; title?: string }>(
  items: T[],
  c: ScreenCopyApi,
): T[] {
  return items.map((it) =>
    it.label !== undefined
      ? { ...it, label: c.opt(it.id, it.label) }
      : it.title !== undefined
        ? { ...it, title: c.opt(it.id, it.title) }
        : it,
  );
}
